import { useEffect } from 'react';

interface CustomCodeInjectorProps {
    css: string | undefined;
    javascript: string | undefined;
}

/**
 * Injects the Component's Design > Custom CSS and Custom JavaScript into
 * the surrounding document. Matches legacy `configurator.js` behaviour
 * (custom_css appended as <style>, custom_javascript appended as <script>).
 *
 * Mounts/unmounts the elements with the component lifecycle so a hot-
 * reloaded preview iframe doesn't accumulate duplicate <style> blocks
 * after every save.
 *
 * Security note: this is **admin-authored** code — the customer of the
 * configurator wrote it themselves in the admin's Design tab — so eval-
 * style execution is the explicit feature, not a third-party-injection
 * concern. Same trust model as the legacy engine.
 */
export function CustomCodeInjector({ css, javascript }: CustomCodeInjectorProps) {
    useEffect(() => {
        if (!css || css.trim().length === 0) return;
        const el = document.createElement('style');
        el.setAttribute('data-configento-custom-css', '');
        el.textContent = css;
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, [css]);

    useEffect(() => {
        if (!javascript || javascript.trim().length === 0) return;
        // We CAN'T use `<script>` with textContent inside React's render —
        // the browser doesn't execute scripts that arrive via innerHTML/
        // createElement+textContent during a foreign insertion path UNLESS
        // we go through document.createElement + appendChild manually.
        // Wrapping in a try/catch keeps a broken snippet from blanking the
        // whole configurator.
        const el = document.createElement('script');
        el.setAttribute('data-configento-custom-js', '');
        el.type = 'text/javascript';
        el.text = javascript;
        try {
            document.head.appendChild(el);
        } catch (err) {
            console.error('[Configento] Custom JavaScript failed to inject:', err);
        }
        return () => {
            el.remove();
        };
    }, [javascript]);

    return null;
}
